import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

// ── Search index ─────────────────────────────────────────────────────────────
const SEARCH_INDEX = [
  { label: 'Home',        sub: 'Page',     path: '/',          hash: '#Hero'      },
  { label: 'About Us',    sub: 'Section',  path: '/',          hash: '#about'     },
  { label: 'Services',    sub: 'Section',  path: '/',          hash: '#services'  },
  { label: 'Our Team',    sub: 'Section',  path: '/',          hash: '#team'      },
  { label: 'Locations',   sub: 'Section',  path: '/',          hash: '#locations' },
  { label: 'Gallery',     sub: 'Page',     path: '/gallery',   hash: null         },
  { label: 'Team',        sub: 'Page',     path: '/team',      hash: null         },
  { label: 'Haircuts',    sub: 'Services', path: '/',          hash: '#services'  },
  { label: 'Beard Trims & Styling', sub: 'Services', path: '/', hash: '#services' },
  { label: 'Nose Waxing', sub: 'Services', path: '/',          hash: '#services'  },
  { label: 'Head Massages', sub: 'Services', path: '/',        hash: '#services'  },
  { label: 'Eyebrow Threading', sub: 'Services', path: '/',    hash: '#services'  },
  { label: 'Facials',     sub: 'Services', path: '/',          hash: '#services'  },
  { label: 'Tattoos',     sub: 'Services', path: '/',          hash: '#services'  },
  { label: 'Abbotsford',  sub: 'Location', path: '/',          hash: '#locations' },
  { label: 'Surrey',      sub: 'Location', path: '/',          hash: '#locations' },
  { label: 'Book Now',    sub: 'Booking',  path: null,         hash: null, action: 'book' },
  { label: 'Contact',     sub: 'Booking',  path: null,         hash: null, action: 'book' },
];

function searchIndex(query) {
  const q = query.toLowerCase().trim();
  if (!q) return [];
  return SEARCH_INDEX.filter(
    item =>
      item.label.toLowerCase().includes(q) ||
      item.sub.toLowerCase().includes(q)
  ).slice(0, 7);
}
// ─────────────────────────────────────────────────────────────────────────────

const SearchIcon = () => (
  <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="7.5" r="5" />
    <line x1="11.5" y1="11.5" x2="16" y2="16" />
  </svg>
);

const Navigation = ({ onContactClick }) => {
  const [scrolled,          setScrolled]          = useState(false);
  const [menuOpen,          setMenuOpen]           = useState(false);
  const [searchExpanded,    setSearchExpanded]     = useState(false);
  const [searchOverlayOpen, setSearchOverlayOpen]  = useState(false);
  const [searchQuery,       setSearchQuery]        = useState('');
  const [searchFocused,     setSearchFocused]      = useState(false);

  const searchRef       = useRef(null);
  const inputRef        = useRef(null);
  const overlayInputRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const isHome   = location.pathname === '/';

  const SQUIRE_BOOKING_URL = 'https://getsquire.com/booking/brands/bro-code-barbershop';

  // ── Scroll listener ──
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── Close search on route change ──
  useEffect(() => {
    setMenuOpen(false);
    setSearchExpanded(false);
    setSearchOverlayOpen(false);
    setSearchQuery('');
    setSearchFocused(false);
  }, [location]);

  // ── Collapse desktop search on outside click ──
  useEffect(() => {
    if (!searchExpanded) return;
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchExpanded(false);
        setSearchQuery('');
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [searchExpanded]);

  // ── Auto-focus desktop input ──
  useEffect(() => {
    if (searchExpanded) setTimeout(() => inputRef.current?.focus(), 40);
  }, [searchExpanded]);

  // ── Auto-focus overlay input ──
  useEffect(() => {
    if (searchOverlayOpen) setTimeout(() => overlayInputRef.current?.focus(), 60);
  }, [searchOverlayOpen]);

  // ── Handle result click ──
  const handleResultClick = (item) => {
    setSearchQuery('');
    setSearchFocused(false);
    setSearchOverlayOpen(false);
    setSearchExpanded(false);

    if (item.action === 'book') {
      onContactClick();
      return;
    }

    if (item.hash) {
      if (isHome || item.path === '/') {
        // Already on home — just scroll
        navigate(item.path + item.hash);
        setTimeout(() => {
          const el = document.querySelector(item.hash);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 80);
      } else {
        navigate(item.path + item.hash);
      }
    } else {
      navigate(item.path);
      window.scrollTo(0, 0);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Escape') {
      setSearchQuery('');
      setSearchFocused(false);
      setSearchOverlayOpen(false);
      setSearchExpanded(false);
    }
    if (e.key === 'Enter' && searchResults.length > 0) {
      handleResultClick(searchResults[0]);
    }
  };

  const closeOverlay = () => {
    setSearchOverlayOpen(false);
    setSearchQuery('');
  };

  const closeMenu = () => setMenuOpen(false);

  const searchResults = searchIndex(searchQuery);
  const showDropdown  = searchFocused && searchQuery.trim().length > 0 && searchResults.length > 0;

  // Hash links work on home, navigate to /#section on other pages
  const hashHref = (hash) => isHome ? hash : `/${hash}`;

  const navLinks = [
    { href: hashHref('#Hero'),      label: 'Home',      isHash: true },
    { href: hashHref('#about'),     label: 'About',     isHash: true },
    { href: hashHref('#services'),  label: 'Services',  isHash: true },
    { href: '/gallery',             label: 'Gallery',   isHash: false },
    { href: '/team',                label: 'Our Team',  isHash: false },
    { href: hashHref('#locations'), label: 'Locations', isHash: true },
  ];

  return (
    <>
      <nav className={`nav ${scrolled ? 'nav--scrolled' : ''}`}>
        <div className="nav__logo">
          <Link to="/">
            <img src="/logo.png" alt="Brocode Barbershop" style={{ width: 54, height: 54 }} />
          </Link>
        </div>

        <ul className="nav__links">
          {navLinks.map(({ href, label, isHash }, i) => (
            <li key={i}>
              {isHash
                ? <a href={href}>{label}</a>
                : <Link to={href} className={location.pathname === href ? 'nav__link--active' : ''}>{label}</Link>
              }
            </li>
          ))}

          {/* ── Desktop search ── */}
          <li>
            <div className="bs-search-wrap" ref={searchRef}>
              {!searchExpanded ? (
                <button
                  className="bs-search-icon-btn"
                  onClick={() => setSearchExpanded(true)}
                  aria-label="Search"
                >
                  <SearchIcon />
                </button>
              ) : (
                <div className={`bs-search-pill${searchFocused ? ' focused' : ''}`}>
                  <span className="bs-search-icon"><SearchIcon /></span>
                  <input
                    ref={inputRef}
                    className="bs-search-input"
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                    onKeyDown={handleKey}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {searchQuery && (
                    <button
                      className="bs-search-clear"
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => { setSearchQuery(''); inputRef.current?.focus(); }}
                      aria-label="Clear"
                    >✕</button>
                  )}
                </div>
              )}

              {showDropdown && (
                <div className="bs-search-results">
                  {searchResults.map((item, i) => (
                    <button
                      key={i}
                      className="bs-search-item"
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => handleResultClick(item)}
                    >
                      <span className="bs-search-item-label">{item.label}</span>
                      <span className="bs-search-item-sub">{item.sub}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </li>

          <li>
            <a
              onClick={() => {
                window.gtag?.('event', 'conversion', { send_to: 'AW-11480037647/YvQ4CIOw3I0cEI_yjeIq' });
              }}
              href="https://getsquire.com/booking/brands/bro-code-barbershop"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontFamily: "'Oswald', 'Impact', 'Arial Narrow', sans-serif",
                fontSize: '0.72rem',
                fontWeight: 600,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#0C0C0C',
                background: '#C6A24A',
                padding: '0.5rem 1.2rem',
                cursor: 'pointer',
                border: 'none',
                transition: 'opacity 0.2s ease',
                display: 'inline-block',
                textDecoration: 'none',
                textAlign: 'center',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Book Now
            </a>
          </li>
        </ul>

        {/* ── Mobile: search icon + burger ── */}
        <div className="nav__mobile-right">
          <button
            className="bs-search-mobile-btn"
            onClick={() => setSearchOverlayOpen(true)}
            aria-label="Search"
          >
            <SearchIcon />
          </button>
          <button
            className={`nav__hamburger ${menuOpen ? 'nav__hamburger--open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ── Mobile menu ── */}
      <div className={`nav__mobile-menu ${menuOpen ? 'nav__mobile-menu--open' : ''}`}>
        <ul className="nav__mobile-links">
          {navLinks.map(({ href, label, isHash }, i) => (
            <li key={i} style={{ '--i': i }}>
              {isHash
                ? <a href={href} onClick={closeMenu}>{label}</a>
                : <Link to={href} onClick={closeMenu}>{label}</Link>
              }
            </li>
          ))}
          <li style={{ '--i': navLinks.length }}>
            <a href="#" onClick={(e) => { e.preventDefault(); closeMenu(); onContactClick(); }}>
              Book Now
            </a>
          </li>
        </ul>
      </div>

      {/* ── Mobile search overlay ── */}
      <AnimatePresence>
        {searchOverlayOpen && (
          <motion.div
            className="bs-search-overlay"
            onClick={e => e.target === e.currentTarget && closeOverlay()}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          >
            <button className="bs-search-overlay-close" onClick={closeOverlay}>CLOSE</button>

            <motion.div
              className="bs-search-overlay-inner"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.34, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="bs-search-overlay-hint">WHERE TO?</p>

              <div className="bs-search-overlay-field">
                <span className="bs-search-overlay-icon"><SearchIcon /></span>
                <input
                  ref={overlayInputRef}
                  className="bs-search-overlay-input"
                  type="text"
                  placeholder="Search anything..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={handleKey}
                  autoComplete="off"
                  spellCheck={false}
                />
                {searchQuery && (
                  <button
                    className="bs-search-clear"
                    onClick={() => { setSearchQuery(''); overlayInputRef.current?.focus(); }}
                    aria-label="Clear"
                  >✕</button>
                )}
              </div>

              {searchQuery.trim().length > 0 && (
                <div className="bs-search-overlay-results">
                  {searchResults.length > 0 ? searchResults.map((item, i) => (
                    <button
                      key={i}
                      className="bs-search-overlay-item"
                      onClick={() => handleResultClick(item)}
                    >
                      <span className="bs-search-overlay-item-label">{item.label}</span>
                      <span className="bs-search-overlay-item-sub">{item.sub}</span>
                    </button>
                  )) : (
                    <p className="bs-search-overlay-empty">No results for &ldquo;{searchQuery}&rdquo;</p>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        /* ── Nav mobile right group ── */
        .nav__mobile-right {
          display: none;
          align-items: center;
          gap: 8px;
        }

        /* ── Desktop search wrapper ── */
        .bs-search-wrap { position: relative; }

        /* Icon-only button */
        .bs-search-icon-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          border: 1px solid rgba(198,162,74,0.28);
          background: rgba(198,162,74,0.06);
          color: rgba(255,255,255,0.55);
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, color 0.2s;
        }
        .bs-search-icon-btn svg { width: 14px; height: 14px; }
        .bs-search-icon-btn:hover {
          background: rgba(198,162,74,0.14);
          border-color: var(--gold);
          color: var(--gold);
        }

        /* Expanded pill */
        .bs-search-pill {
          display: flex;
          align-items: center;
          gap: 6px;
          background: rgba(198,162,74,0.06);
          border: 1px solid rgba(198,162,74,0.28);
          border-radius: 100px;
          padding: 5px 10px 5px 9px;
          width: 200px;
          animation: bsSearchExpand 0.26s cubic-bezier(0.16,1,0.3,1) both;
        }
        @keyframes bsSearchExpand {
          from { width: 34px; opacity: 0.2; }
          to   { width: 200px; opacity: 1; }
        }
        .bs-search-pill.focused {
          border-color: var(--gold);
          background: rgba(198,162,74,0.1);
        }
        .bs-search-icon {
          width: 13px;
          height: 13px;
          color: rgba(255,255,255,0.4);
          flex-shrink: 0;
          display: flex;
          align-items: center;
        }
        .bs-search-icon svg { width: 13px; height: 13px; }
        .bs-search-input {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          color: #fff;
          font-size: 11px;
          font-family: var(--ff-ui);
          font-weight: 400;
          min-width: 0;
          letter-spacing: 0.02em;
        }
        .bs-search-input::placeholder { color: rgba(255,255,255,0.25); }
        .bs-search-clear {
          background: none;
          border: none;
          color: rgba(255,255,255,0.3);
          font-size: 9px;
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
          transition: color 0.15s;
        }
        .bs-search-clear:hover { color: var(--gold); }

        /* Desktop dropdown */
        .bs-search-results {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          min-width: 260px;
          background: #111;
          border: 1px solid rgba(198,162,74,0.18);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 16px 48px rgba(0,0,0,0.6);
          z-index: 200;
        }
        .bs-search-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 11px 14px;
          background: none;
          border: none;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          cursor: pointer;
          text-align: left;
          transition: background 0.15s;
          gap: 12px;
        }
        .bs-search-item:last-child { border-bottom: none; }
        .bs-search-item:hover { background: rgba(198,162,74,0.08); }
        .bs-search-item-label {
          font-size: 12px;
          color: #fff;
          font-family: var(--ff-ui);
          font-weight: 400;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .bs-search-item-sub {
          font-size: 9px;
          color: var(--gold);
          font-family: var(--ff-display);
          letter-spacing: 0.12em;
          white-space: nowrap;
          flex-shrink: 0;
          text-transform: uppercase;
          opacity: 0.7;
        }

        /* ── Mobile search button ── */
        .bs-search-mobile-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1px solid rgba(198,162,74,0.28);
          background: rgba(198,162,74,0.06);
          color: rgba(255,255,255,0.65);
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s;
        }
        .bs-search-mobile-btn svg { width: 14px; height: 14px; }
        .bs-search-mobile-btn:hover {
          background: rgba(198,162,74,0.14);
          border-color: var(--gold);
          color: var(--gold);
        }

        /* ── Search overlay (full screen) ── */
        .bs-search-overlay {
          position: fixed;
          inset: 0;
          z-index: 9998;
          background: rgba(12,12,12,0.92);
          backdrop-filter: blur(28px);
          -webkit-backdrop-filter: blur(28px);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding-top: max(80px, env(safe-area-inset-top, 80px));
          padding-left: 20px;
          padding-right: 20px;
        }
        .bs-search-overlay-close {
          position: absolute;
          top: max(16px, env(safe-area-inset-top, 16px));
          right: 16px;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          border: 1px solid rgba(198,162,74,0.3);
          background: transparent;
          color: var(--gold);
          font-family: var(--ff-display);
          font-size: 8px;
          letter-spacing: 0.16em;
          cursor: pointer;
          transition: background 0.25s, color 0.25s;
        }
        .bs-search-overlay-close:hover {
          background: var(--gold);
          color: #000;
        }
        .bs-search-overlay-inner {
          width: 100%;
          max-width: 540px;
        }
        .bs-search-overlay-hint {
          font-family: var(--ff-display);
          font-size: 10px;
          letter-spacing: 0.3em;
          color: var(--gold);
          opacity: 0.6;
          margin: 0 0 20px 2px;
          text-transform: uppercase;
        }
        .bs-search-overlay-field {
          display: flex;
          align-items: center;
          gap: 12px;
          border-bottom: 1px solid rgba(198,162,74,0.25);
          padding-bottom: 14px;
          margin-bottom: 24px;
        }
        .bs-search-overlay-icon {
          color: rgba(255,255,255,0.4);
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }
        .bs-search-overlay-icon svg { width: 20px; height: 20px; }
        .bs-search-overlay-input {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          color: #fff;
          font-size: clamp(1.4rem, 5vw, 2rem);
          font-family: var(--ff-display);
          font-weight: 400;
          letter-spacing: 0.02em;
          min-width: 0;
        }
        .bs-search-overlay-input::placeholder { color: rgba(255,255,255,0.18); }
        .bs-search-overlay-results {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .bs-search-overlay-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 14px 16px;
          background: rgba(198,162,74,0.04);
          border: 1px solid rgba(198,162,74,0.1);
          border-radius: 10px;
          cursor: pointer;
          text-align: left;
          transition: background 0.15s, border-color 0.15s;
          gap: 16px;
        }
        .bs-search-overlay-item:hover {
          background: rgba(198,162,74,0.1);
          border-color: rgba(198,162,74,0.28);
        }
        .bs-search-overlay-item-label {
          font-size: 15px;
          color: #fff;
          font-family: var(--ff-display);
          font-weight: 400;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        .bs-search-overlay-item-sub {
          font-size: 9px;
          color: var(--gold);
          font-family: var(--ff-display);
          letter-spacing: 0.16em;
          white-space: nowrap;
          flex-shrink: 0;
          text-transform: uppercase;
          opacity: 0.7;
        }
        .bs-search-overlay-empty {
          font-size: 14px;
          color: rgba(255,255,255,0.3);
          font-family: var(--ff-ui);
          text-align: center;
          padding: 24px 0;
          margin: 0;
        }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .nav__links     { display: none; }
          .nav__hamburger { display: flex; }
          .nav__mobile-right { display: flex; }
        }
      `}</style>
    </>
  );
};

export default Navigation;
